import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "prosemirror-state";
import { CellSelection, cellAround, TableMap } from "@tiptap/pm/tables";
import { DOMParser as ProseMirrorDOMParser } from "@tiptap/pm/model";

export const TableCellPaste = Extension.create({
    name: "tableCellPaste",

    addProseMirrorPlugins() {
        const editor = this.editor;

        return [
            new Plugin({
                key: new PluginKey("tableCellPaste"),
                props: {
                    handlePaste(view, event, slice) {
                        const { state } = view;

                        const clipboardData = event.clipboardData || window.clipboardData;
                        if (!clipboardData) {
                            return false;
                        }

                        const html = clipboardData.getData("text/html");
                        if (!html) {
                            return false;
                        }

                        // Parse the HTML to check for cell-paste metadata
                        const tempDiv = document.createElement("div");
                        tempDiv.innerHTML = html;

                        const pasteTable = tempDiv.querySelector("table[data-cell-paste]");
                        if (!pasteTable) {
                            return false;
                        }

                        // This is a cell-paste operation - find the target cell
                        let $cell = null;

                        if (state.selection instanceof CellSelection) {
                            // Multi-cell selection - use the anchor cell
                            $cell = state.selection.$anchorCell;
                        } else {
                            // TextSelection or other - use cellAround to find the cell
                            const $from = state.selection.$from;

                            $cell = cellAround($from);

                            // If cellAround fails, try manual traversal
                            if (!$cell) {
                                for (let d = $from.depth - 1; d > 0; d--) {
                                    const node = $from.node(d);
                                    if (
                                        node.type.spec.tableRole === "row" ||
                                        node.type.name === "tableRow"
                                    ) {
                                        // Found the row, cell is at d + 1
                                        const cellPos = $from.before(d + 1);
                                        $cell = state.doc.resolve(cellPos);
                                        break;
                                    }
                                }
                            }
                        }

                        // If we can't find a cell, let the default paste behavior handle it
                        if (!$cell) {
                            return false;
                        }

                        // We're in a table cell with cell-paste data - we'll handle this
                        const pasteRows = parseInt(
                            pasteTable.getAttribute("data-paste-rows") || "1",
                        );
                        const pasteCols = parseInt(
                            pasteTable.getAttribute("data-paste-cols") || "1",
                        );

                        try {
                            // Get the table using relative depth (-1 from cell)
                            // $cell is positioned before the cell, so $cell.node(-1) gets the row's parent = table
                            const table = $cell.node(-1);
                            if (!table || table.type.spec.tableRole !== "table") {
                                return false;
                            }

                            // Get the table start position (position after table opening tag)
                            const tableStart = $cell.start(-1);
                            const map = TableMap.get(table);

                            // Calculate the cell's position relative to the table
                            // $cell.pos is the absolute position before the cell
                            // tableStart is the position after the table opening tag
                            const cellPos = $cell.pos - tableStart;

                            // Find the cell's row and column in the map
                            const cellRect = map.findCell(cellPos);
                            const startRow = cellRect.top;
                            const startCol = cellRect.left;

                            // Parse the pasted table cells
                            const pastedCells = parsePastedCells(pasteTable, editor.schema);
                            if (pastedCells.length === 0) {
                                return false;
                            }

                            // Calculate how many rows/cols we actually need to paste
                            const actualPasteRows = Math.min(pasteRows, map.height - startRow);
                            const actualPasteCols = Math.min(pasteCols, map.width - startCol);

                            // Build the transaction
                            let tr = state.tr;
                            let modified = false;

                            // Create a grid to track which cells we've already processed
                            // (to handle merged cells in target table)
                            const processedCells = new Set();

                            for (let rowOffset = 0; rowOffset < actualPasteRows; rowOffset++) {
                                const targetRow = startRow + rowOffset;
                                if (targetRow >= map.height) {
                                    break;
                                }

                                for (let colOffset = 0; colOffset < actualPasteCols; colOffset++) {
                                    const targetCol = startCol + colOffset;
                                    if (targetCol >= map.width) {
                                        break;
                                    }

                                    // Get the target cell position from the table map
                                    const targetCellPos =
                                        map.map[targetRow * map.width + targetCol];
                                    const absoluteCellPos = tableStart + targetCellPos;

                                    // Skip if we've already processed this cell (due to merged cells)
                                    if (processedCells.has(targetCellPos)) {
                                        continue;
                                    }
                                    processedCells.add(targetCellPos);

                                    // Get the target cell
                                    const targetCell = table.nodeAt(targetCellPos);
                                    if (!targetCell) {
                                        continue;
                                    }

                                    // Find the corresponding pasted cell content
                                    const pastedCell = findPastedCell(
                                        pastedCells,
                                        rowOffset,
                                        colOffset,
                                    );
                                    if (!pastedCell) {
                                        continue;
                                    }

                                    // Replace the cell content
                                    // We need to map positions through previous changes
                                    const mappedPos = tr.mapping.map(absoluteCellPos);

                                    // Get the current cell at the mapped position
                                    const $mappedPos = tr.doc.resolve(mappedPos);
                                    const currentCell = $mappedPos.nodeAfter;

                                    if (
                                        currentCell &&
                                        (currentCell.type.name === "tableCell" ||
                                            currentCell.type.name === "tableHeader")
                                    ) {
                                        // Calculate the range to replace (cell content only, not the cell itself)
                                        const cellContentStart = mappedPos + 1;
                                        const cellContentEnd = mappedPos + currentCell.nodeSize - 1;

                                        // Replace cell content with pasted content
                                        tr = tr.replaceWith(
                                            cellContentStart,
                                            cellContentEnd,
                                            pastedCell.content,
                                        );
                                        modified = true;
                                    }
                                }
                            }

                            if (modified) {
                                view.dispatch(tr);
                                return true;
                            }

                            return false;
                        } catch (e) {
                            console.error("Error in cell paste:", e);
                            return false;
                        }
                    },
                },
            }),
        ];
    },
});

/**
 * Parse the pasted table and extract cells with their content
 */
function parsePastedCells(tableElement, schema) {
    const cells = [];
    const tbody = tableElement.querySelector("tbody") || tableElement;
    const rows = tbody.querySelectorAll("tr");

    let rowIndex = 0;
    const cellGrid = []; // Track cell positions including rowspans

    rows.forEach((row) => {
        const tds = row.querySelectorAll("td, th");
        let colIndex = 0;

        // Initialize row in grid if needed
        cellGrid[rowIndex] = cellGrid[rowIndex] || [];

        // Skip columns that are occupied by rowspanned cells from previous rows
        while (cellGrid[rowIndex][colIndex]) {
            colIndex++;
        }

        tds.forEach((td) => {
            // Skip columns occupied by rowspanned cells
            while (cellGrid[rowIndex][colIndex]) {
                colIndex++;
            }

            const colspan = parseInt(td.getAttribute("colspan") || "1");
            const rowspan = parseInt(td.getAttribute("rowspan") || "1");

            // Parse cell content using ProseMirror's DOM parser
            const content = parseCellContent(td, schema);

            // Mark grid positions as occupied
            for (let r = 0; r < rowspan; r++) {
                for (let c = 0; c < colspan; c++) {
                    cellGrid[rowIndex + r] = cellGrid[rowIndex + r] || [];
                    cellGrid[rowIndex + r][colIndex + c] = true;
                }
            }

            cells.push({
                row: rowIndex,
                col: colIndex,
                colspan,
                rowspan,
                content,
                element: td,
            });

            colIndex += colspan;
        });

        rowIndex++;
    });

    return cells;
}

/**
 * Parse the content of a table cell into ProseMirror nodes
 */
function parseCellContent(cellElement, schema) {
    // Create a temporary document from the cell content
    const tempDoc = document.implementation.createHTMLDocument();
    tempDoc.body.innerHTML = cellElement.innerHTML;

    // Use ProseMirror's DOM parser to convert to nodes
    const parser = ProseMirrorDOMParser.fromSchema(schema);

    try {
        // Parse the content
        const parsed = parser.parse(tempDoc.body);

        // Return the content fragment
        return parsed.content;
    } catch (e) {
        console.error("Error parsing cell content:", e);
        // Return an empty paragraph as fallback
        return schema.nodes.paragraph.create().content;
    }
}

/**
 * Find the pasted cell content for a given row/column offset
 * Handles merged cells by finding the cell that covers the position
 */
function findPastedCell(pastedCells, rowOffset, colOffset) {
    // First, try to find exact match
    let cell = pastedCells.find((c) => c.row === rowOffset && c.col === colOffset);

    if (cell) {
        return cell;
    }

    // If not found, check if this position is covered by a merged cell
    for (const c of pastedCells) {
        const rowEnd = c.row + c.rowspan;
        const colEnd = c.col + c.colspan;

        if (rowOffset >= c.row && rowOffset < rowEnd && colOffset >= c.col && colOffset < colEnd) {
            // This position is covered by a merged cell
            // Only return content for the anchor position
            if (rowOffset === c.row && colOffset === c.col) {
                return c;
            }
            // For spanned positions, return null (skip pasting)
            return null;
        }
    }

    return null;
}

export default TableCellPaste;
